import { useEffect, useRef, useState } from "react";
import { Navigate } from "react-router-dom";
import { motion, useScroll, useTransform, useInView } from "framer-motion";
import Marquee from "react-fast-marquee";
import {
  ArrowUpRight, Heartbeat, Pulse, ShieldCheck, Lightning, Sparkle, Cube,
  Lifebuoy, Brain, Stethoscope, Syringe, FirstAid, ArrowDown, Waveform,
  Fingerprint, ChartLineUp, Wallet, Users, Cpu, Trophy, Scan, Target as TargetIcon,
} from "@phosphor-icons/react";
import { useAuth } from "../context/AuthContext";

/* ============ Assets ============ */
const IMG_ICU     = "https://images.pexels.com/photos/6291261/pexels-photo-6291261.jpeg?auto=compress&w=1400";
const IMG_SCANNER = "https://images.pexels.com/photos/7108390/pexels-photo-7108390.jpeg?auto=compress&w=1200";
const IMG_ULTRA   = "https://images.pexels.com/photos/7108402/pexels-photo-7108402.jpeg?auto=compress&w=1400";
const IMG_LIGHTS  = "https://images.pexels.com/photos/19563295/pexels-photo-19563295.jpeg?auto=compress&w=1400";
const IMG_DOCTOR  = "https://images.pexels.com/photos/4173251/pexels-photo-4173251.jpeg?auto=compress&w=1200";

/* ============ Small primitives ============ */
function useCountUp(target, duration = 1600) {
  const [n, setN] = useState(0);
  useEffect(() => {
    let raf, start;
    const tick = (t) => {
      if (!start) start = t;
      const p = Math.min(1, (t - start) / duration);
      setN(Math.floor(p * target));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, duration]);
  return n;
}

/* Custom cursor red dot that follows mouse */
function CursorDot() {
  const ref = useRef(null);
  useEffect(() => {
    const dot = ref.current;
    if (!dot) return;
    const move = (e) => {
      dot.style.transform = `translate3d(${e.clientX - 8}px, ${e.clientY - 8}px, 0)`;
    };
    window.addEventListener("mousemove", move);
    return () => window.removeEventListener("mousemove", move);
  }, []);
  return (
    <div
      ref={ref}
      className="pointer-events-none fixed z-[9999] hidden md:block"
      style={{
        width: 16, height: 16, top: 0, left: 0,
        background: "#D90429",
        boxShadow: "0 0 16px 4px rgba(217,4,41,0.55)",
        mixBlendMode: "difference",
        transition: "transform 0.02s linear",
      }}
    />
  );
}

/* Grain / noise overlay for the whole page */
function Grain() {
  return (
    <div
      aria-hidden
      className="pointer-events-none fixed inset-0 z-[1] opacity-[0.05]"
      style={{
        backgroundImage:
          "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='140' height='140'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%' height='100%' filter='url(%23n)' opacity='0.6'/></svg>\")",
      }}
    />
  );
}

function LiveDot({ color = "#D90429", label }) {
  return (
    <span className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-400">
      <span className="relative flex h-2 w-2">
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full opacity-70" style={{ background: color }} />
        <span className="relative inline-flex h-2 w-2 rounded-full" style={{ background: color }} />
      </span>
      {label}
    </span>
  );
}

/* Kinetic ECG line — SVG that redraws */
function ECGLine({ height = 140 }) {
  return (
    <svg viewBox="0 0 1000 160" preserveAspectRatio="none" style={{ width: "100%", height }} aria-hidden="true">
      <defs>
        <linearGradient id="ecgFade" x1="0" x2="1">
          <stop offset="0%" stopColor="#D90429" stopOpacity="0" />
          <stop offset="20%" stopColor="#D90429" stopOpacity="0.9" />
          <stop offset="80%" stopColor="#D90429" stopOpacity="0.9" />
          <stop offset="100%" stopColor="#D90429" stopOpacity="0" />
        </linearGradient>
      </defs>
      <motion.path
        d="M0 80 L120 80 L150 80 L165 60 L180 110 L195 40 L210 120 L225 80 L350 80 L380 80 L400 30 L420 130 L440 80 L580 80 L605 80 L620 65 L640 100 L660 80 L820 80 L850 80 L870 50 L885 120 L900 80 L1000 80"
        fill="none"
        stroke="url(#ecgFade)"
        strokeWidth="2.5"
        initial={{ pathLength: 0 }}
        animate={{ pathLength: 1 }}
        transition={{ duration: 3.2, ease: "easeInOut", repeat: Infinity, repeatType: "loop" }}
      />
    </svg>
  );
}

/* Sticker badge */
function Sticker({ children, rotate = -6, color = "#D90429" }) {
  return (
    <span
      className="inline-flex items-center gap-1.5 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] font-bold"
      style={{
        background: color,
        color: "#0A0A0A",
        transform: `rotate(${rotate}deg)`,
        boxShadow: "3px 3px 0 rgba(0,0,0,0.85)",
      }}
    >
      {children}
    </span>
  );
}

/* Brutalist card w/ hover shift */
function BrutalCard({ children, className = "", accent = "#D90429", href, testId }) {
  const Wrapper = href ? "a" : "div";
  return (
    <Wrapper
      href={href}
      data-testid={testId}
      className={`group relative block bg-[#111114] border border-white/10 p-6 md:p-8 transition-transform duration-200 hover:-translate-x-1 hover:-translate-y-1 ${className}`}
      style={{ boxShadow: "6px 6px 0 rgba(255,255,255,0.06)" }}
    >
      <div
        className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none"
        style={{ boxShadow: `6px 6px 0 ${accent}` }}
      />
      {children}
    </Wrapper>
  );
}

/* ============ Sections ============ */
function TopBar() {
  return (
    <div className="relative z-20 border-b border-white/10 bg-black text-white">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-9 flex items-center justify-between text-[10px] font-mono uppercase tracking-[0.25em]">
        <div className="flex items-center gap-5">
          <LiveDot label="INDIA · OPERATIONAL" />
          <span className="hidden md:inline text-white/50">12 CITIES · 3 ENTITIES</span>
          <span className="hidden lg:inline text-white/40">CE · ISO 13485 · WHO-GMP</span>
        </div>
        <div className="hidden md:flex items-center gap-4 text-white/50">
          <span>V1.6</span>
          <span>·</span>
          <span>SHELVES TECH PVT LTD</span>
        </div>
      </div>
    </div>
  );
}

function Nav({ onLogin }) {
  return (
    <header className="relative z-20 border-b border-white/10 bg-black/80 backdrop-blur-lg">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
        <a href="#top" className="flex items-baseline gap-3">
          <span className="font-display text-2xl font-black tracking-tighter text-white">REYANSH</span>
          <span className="hidden md:inline font-mono text-[10px] uppercase tracking-[0.25em] text-[#D90429]">/ BY SHELVES TECH</span>
        </a>
        <nav className="hidden md:flex items-center gap-8 font-mono text-[11px] uppercase tracking-[0.2em] text-white/70">
          <a href="#modules"    className="hover:text-white transition">Modules</a>
          <a href="#verticals"  className="hover:text-white transition">Verticals</a>
          <a href="#proof"      className="hover:text-white transition">Proof</a>
          <a href="#founder"    className="hover:text-white transition">Founder</a>
        </nav>
        <button
          onClick={onLogin}
          data-testid="header-login-button"
          className="group relative overflow-hidden bg-[#D90429] text-white px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.2em] font-bold border border-[#D90429] hover:bg-transparent hover:text-[#D90429] transition-colors"
        >
          <span className="relative z-10 flex items-center gap-2">Sign in <ArrowUpRight size={14} weight="bold" /></span>
        </button>
      </div>
    </header>
  );
}

function Hero({ onLogin }) {
  const { scrollY } = useScroll();
  const y1 = useTransform(scrollY, [0, 800], [0, -100]);

  return (
    <section id="top" className="relative overflow-hidden bg-black text-white">
      {/* Red radial glow */}
      <div className="absolute -top-40 -right-40 h-[600px] w-[600px] rounded-full opacity-30 blur-[120px]" style={{ background: "#D90429" }} />
      <div className="absolute top-1/2 -left-40 h-[400px] w-[400px] rounded-full opacity-20 blur-[120px]" style={{ background: "#D90429" }} />

      <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-24">
        {/* Overline */}
        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="flex flex-wrap items-center gap-4 mb-8"
        >
          <Sticker rotate={-3}>THE OS · v1.6</Sticker>
          <span className="font-mono text-[11px] uppercase tracking-[0.3em] text-white/60">Reyansh operating system · Shelves Tech</span>
        </motion.div>

        {/* HUGE KINETIC HEADLINE */}
        <div className="relative xl:pr-[340px]">
          <motion.h1
            initial={{ opacity: 0, y: 60 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="font-display font-black tracking-[-0.04em] leading-[0.85] text-[clamp(3.5rem,11vw,10rem)] uppercase"
          >
            <span className="block">Hospitals</span>
            <span className="block">
              run on <span className="italic font-serif font-black text-[#D90429]">chaos.</span>
            </span>
            <span className="block relative">
              <span className="text-white/20">We ship</span>
              <br />
              <span className="text-white">the fix.</span>
            </span>
          </motion.h1>

          {/* Punchy sub-tagline — CRM redefined */}
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.6 }}
            className="mt-8 md:mt-10 flex flex-wrap items-baseline gap-x-4 gap-y-2 font-mono uppercase tracking-[0.15em] text-sm md:text-base"
            data-testid="crm-tagline"
          >
            <span className="text-white/40">CRM</span>
            <span className="text-[#D90429] text-lg md:text-xl font-black">=</span>
            <span className="text-white font-bold">Close</span>
            <span className="text-white font-bold">Relationships</span>
            <span className="text-white font-bold">Matter<span className="text-[#D90429]">.</span></span>
          </motion.div>

          {/* Floating right-side bento widgets */}
          <motion.div style={{ y: y1 }} className="hidden xl:block absolute -right-2 top-20 w-[300px] space-y-3 pointer-events-none">
            <div className="bg-white text-black p-4 border border-white/10 pointer-events-auto" style={{ boxShadow: "6px 6px 0 #D90429" }}>
              <div className="flex items-center justify-between mb-2">
                <span className="font-mono text-[9px] uppercase tracking-[0.25em] text-[#D90429] font-bold">Live · ICU-04</span>
                <LiveDot color="#10b981" label="ONLINE" />
              </div>
              <div className="font-display text-4xl font-black tracking-tighter leading-none">84 <span className="text-lg font-mono text-black/50">bpm</span></div>
              <ECGLine height={44} />
            </div>
            <div className="bg-[#D90429] text-white p-4 pointer-events-auto" style={{ boxShadow: "6px 6px 0 #fff" }}>
              <div className="font-mono text-[9px] uppercase tracking-[0.25em] font-bold mb-1 text-white/80">This week</div>
              <div className="font-display text-3xl font-black tracking-tighter leading-none">₹47.2L</div>
              <div className="text-xs font-mono uppercase tracking-widest mt-1 text-white/70">CASH IN · 12 PAYMENTS</div>
            </div>
            <div className="bg-black border border-white/15 p-4 text-white pointer-events-auto">
              <div className="flex items-center gap-2 mb-2">
                <Fingerprint size={14} weight="bold" className="text-[#D90429]" />
                <span className="font-mono text-[9px] uppercase tracking-[0.25em] text-white/60">RBAC · 5 roles</span>
              </div>
              <div className="font-display text-xl font-bold tracking-tight">One login, seven divisions.</div>
            </div>
          </motion.div>
        </div>

        {/* Subhead + CTA cluster */}
        <motion.div
          initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="mt-14 md:mt-16 grid lg:grid-cols-12 gap-8 items-end"
        >
          <div className="lg:col-span-6">
            <p className="text-white/70 text-lg md:text-xl leading-relaxed max-w-xl">
              Reyansh is <span className="text-white font-semibold">the internal OS</span> Shelves Tech uses to run — leads, cash, procurement, MIS,
              payroll, performance. Built for a med-device team that <span className="text-[#D90429] font-semibold">actually moves.</span>
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <button
                onClick={onLogin}
                data-testid="hero-login-button"
                className="group relative bg-white text-black px-7 py-4 font-mono text-xs uppercase tracking-[0.2em] font-bold border border-white hover:bg-[#D90429] hover:text-white hover:border-[#D90429] transition-colors inline-flex items-center gap-3"
                style={{ boxShadow: "6px 6px 0 #D90429" }}
              >
                Sign in with Google Workspace
                <ArrowUpRight size={16} weight="bold" className="group-hover:rotate-45 transition-transform" />
              </button>
              <a href="#modules" className="font-mono text-[11px] uppercase tracking-[0.25em] text-white/60 hover:text-white transition inline-flex items-center gap-2">
                Explore modules <ArrowDown size={14} weight="bold" />
              </a>
            </div>
          </div>

          <div className="lg:col-span-6 grid grid-cols-3 gap-4">
            <div className="border border-white/15 bg-white/5 p-4">
              <div className="font-mono text-[9px] uppercase tracking-widest text-white/50">Response time</div>
              <div className="font-display text-3xl font-black tracking-tighter mt-1">4h<span className="text-[#D90429]">.</span></div>
            </div>
            <div className="border border-white/15 bg-white/5 p-4">
              <div className="font-mono text-[9px] uppercase tracking-widest text-white/50">Approvals</div>
              <div className="font-display text-3xl font-black tracking-tighter mt-1">/dev/null</div>
            </div>
            <div className="border border-white/15 bg-white/5 p-4">
              <div className="font-mono text-[9px] uppercase tracking-widest text-white/50">Excel sheets</div>
              <div className="font-display text-3xl font-black tracking-tighter mt-1">zero<span className="text-[#D90429]">.</span></div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Bottom kinetic marquee */}
      <div className="relative z-10 border-y border-white/10 bg-black overflow-hidden">
        <Marquee gradient={false} speed={45} className="py-4">
          {[
            "DELIVERING BETTER HEALTHCARE",
            "★",
            "12 CITIES",
            "★",
            "74+ HOSPITALS",
            "★",
            "GEMINI-INDEXED",
            "★",
            "ISO 13485 CERTIFIED",
            "★",
            "SINCE 2019",
            "★",
          ].map((t, i) => (
            <span key={i} className="mx-8 font-display text-3xl md:text-5xl font-black tracking-tighter uppercase text-white/90">
              {t}
            </span>
          ))}
        </Marquee>
      </div>
    </section>
  );
}

/* Modules — bento grid, 7 divisions */
const MODULES = [
  { id: "crm",         Icon: Users,       label: "Sales & CRM",       tag: "REVENUE",       color: "#D90429", copy: "Leads, pipeline, AI Swarm, WhatsApp dispatch. If it closes, it lives here." },
  { id: "finance",     Icon: Wallet,      label: "Finance",           tag: "CASH FLOW",     color: "#10b981", copy: "Voucher AI → Busy. GST split. Cash-in / who-owes-us. Zero-Excel policy enforced." },
  { id: "procurement", Icon: Cpu,         label: "Procurement",       tag: "SUPPLY",        color: "#f59e0b", copy: "Item master, vendor ledger, live margins. Gemini parses invoice PDFs on drop." },
  { id: "hr",          Icon: Trophy,      label: "HR & Performance",  tag: "PEOPLE",        color: "#8b5cf6", copy: "Onboarding, letters, monthly leaderboard, one-pager PDF for employee of the month." },
  { id: "mis",         Icon: Scan,        label: "MIS Productivity",  tag: "OPERATIONS",    color: "#3b82f6", copy: "Daily page logs, per-entity revenue, targets vs actuals. Sundays excluded, by policy." },
  { id: "tracker",     Icon: TargetIcon,  label: "Shelves-Track",     tag: "ACCOUNTABILITY", color: "#ef4444", copy: "Deadline HUD that refuses to be dismissed. Overdue tasks demand a written excuse." },
  { id: "admin",       Icon: ShieldCheck, label: "Admin & RBAC",      tag: "GOVERNANCE",    color: "#facc15", copy: "5 roles. Every endpoint gated. Google Workspace SSO. No shared logins, ever." },
];

function ModulesGrid() {
  return (
    <section id="modules" className="relative bg-black text-white py-24 md:py-32">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="grid md:grid-cols-12 gap-4 mb-12">
          <div className="md:col-span-5">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D90429] mb-4">/ MODULES · 07</div>
            <h2 className="font-display text-5xl md:text-7xl font-black tracking-[-0.03em] leading-[0.9] uppercase">
              Seven divisions.<br />
              <span className="text-[#D90429]">One</span> operating surface.
            </h2>
          </div>
          <div className="md:col-span-6 md:col-start-7 flex items-end">
            <p className="text-white/60 text-base md:text-lg leading-relaxed">
              No context switching. No CSV imports. No "let me get back to you". Every division of Shelves Tech operates on Reyansh — from the founder to the field tech.
            </p>
          </div>
        </div>

        <div className="grid md:grid-cols-6 gap-4 auto-rows-[minmax(220px,auto)]">
          {MODULES.map((m, idx) => {
            // Feature spans: CRM & Finance span 3 cols on md; others 2
            const span =
              idx === 0 ? "md:col-span-3 md:row-span-2" :
              idx === 1 ? "md:col-span-3" :
              idx === 2 ? "md:col-span-2" :
              idx === 3 ? "md:col-span-2" :
              idx === 4 ? "md:col-span-2" :
              idx === 5 ? "md:col-span-3" :
                          "md:col-span-3";
            return (
              <ModuleCard key={m.id} m={m} className={span} big={idx === 0} />
            );
          })}
        </div>
      </div>
    </section>
  );
}

function ModuleCard({ m, className, big = false }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-50px" });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      className={`group relative border border-white/10 bg-[#0f0f11] p-6 md:p-8 overflow-hidden ${className}`}
      style={{ transition: "transform 200ms ease" }}
      onMouseEnter={(e) => e.currentTarget.style.transform = "translate(-4px,-4px)"}
      onMouseLeave={(e) => e.currentTarget.style.transform = "translate(0,0)"}
    >
      {/* Accent corner */}
      <div className="absolute top-0 left-0 h-1 w-16" style={{ background: m.color }} />
      <div className="absolute top-0 right-0 flex items-center gap-2 p-4">
        <span className="font-mono text-[9px] uppercase tracking-[0.3em]" style={{ color: m.color }}>{m.tag}</span>
      </div>

      <div className="relative z-10 flex flex-col h-full">
        <div className="mt-8 mb-6">
          <div className="w-12 h-12 flex items-center justify-center border border-white/15 mb-4" style={{ background: `${m.color}18` }}>
            <m.Icon size={22} weight="bold" style={{ color: m.color }} />
          </div>
          <h3 className={`font-display font-black tracking-tight uppercase ${big ? "text-5xl md:text-6xl" : "text-3xl md:text-4xl"}`}>
            {m.label}
          </h3>
        </div>
        <p className={`text-white/60 leading-relaxed ${big ? "text-lg md:text-xl max-w-md" : "text-sm md:text-base"}`}>
          {m.copy}
        </p>

        <div className="mt-auto pt-6 flex items-center justify-between">
          <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40">module_0{MODULES.findIndex(x => x.id === m.id) + 1}</span>
          <ArrowUpRight size={20} weight="bold" className="text-white/40 group-hover:text-white group-hover:rotate-12 transition-all" />
        </div>
      </div>

      {/* Big card: floating ECG on hover */}
      {big && (
        <div className="absolute right-0 bottom-0 w-1/2 opacity-30 group-hover:opacity-60 transition-opacity pointer-events-none">
          <ECGLine height={120} />
        </div>
      )}
    </motion.div>
  );
}

/* Stats band — inverted white block */
function StatsBand() {
  const devices = useCountUp(1240);
  const cr      = useCountUp(42);
  const hosp    = useCountUp(74);
  const pages   = useCountUp(186);

  const stats = [
    { n: `${devices}+`,   l: "MEDICAL DEVICES SHIPPED" },
    { n: `₹${(cr / 10).toFixed(1)} Cr`, l: "ACTIVE PIPELINE" },
    { n: `${hosp}+`,      l: "HOSPITAL PARTNERS" },
    { n: `${pages}0 K+`,  l: "PAGES DIGITIZED" },
  ];

  return (
    <section id="proof" className="relative bg-white text-black py-24 md:py-32 overflow-hidden">
      <div className="absolute top-6 left-6 flex items-center gap-3">
        <LiveDot color="#D90429" label="TRACK RECORD · SINCE 2019" />
      </div>
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="mb-16 md:mb-20">
          <h2 className="font-display text-5xl md:text-7xl font-black tracking-[-0.03em] leading-[0.9] uppercase">
            The receipts.<br />
            <span className="text-[#D90429]">Not the pitch.</span>
          </h2>
        </div>

        <div className="grid md:grid-cols-4 gap-4">
          {stats.map((s, i) => (
            <motion.div
              key={s.l}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: i * 0.08 }}
              className="border-t-4 border-black pt-6"
            >
              <div className="font-display text-6xl md:text-7xl font-black tracking-[-0.04em] leading-none">
                {s.n}
              </div>
              <div className="mt-4 font-mono text-[10px] uppercase tracking-[0.25em] text-black/60">{s.l}</div>
            </motion.div>
          ))}
        </div>

        {/* Regulatory chip strip */}
        <div className="mt-20 flex flex-wrap items-center gap-3">
          {["CE MARK", "ISO 13485", "WHO-GMP", "DGFT · IEC", "MSME REGISTERED", "CDSCO COMPLIANT"].map((c) => (
            <span key={c} className="border-2 border-black px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] font-bold hover:bg-black hover:text-white transition-colors">
              {c}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}

/* Verticals — brutalist medical gallery marquee */
const VERTICALS = [
  { img: IMG_ICU,     label: "ICU & CRITICAL CARE",   code: "V-01" },
  { img: IMG_SCANNER, label: "RADIOLOGY",             code: "V-02" },
  { img: IMG_ULTRA,   label: "DIAGNOSTICS",           code: "V-03" },
  { img: IMG_LIGHTS,  label: "SURGERY & OT",          code: "V-04" },
];

function Verticals() {
  return (
    <section id="verticals" className="relative bg-black text-white py-24 md:py-32 overflow-hidden">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 mb-12">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D90429] mb-4">/ WE DELIVER</div>
            <h2 className="font-display text-5xl md:text-7xl font-black tracking-[-0.03em] leading-[0.9] uppercase">
              From <span className="italic font-serif">syringes</span><br />
              to <span className="text-[#D90429]">ICU beds.</span>
            </h2>
          </div>
          <div className="max-w-sm text-white/60 text-base leading-relaxed">
            Distribution across 8 verticals, 700+ SKUs, direct OEM partnerships with Indian and imported brands. Cold-chain, warranty and biomedical support built-in.
          </div>
        </div>
      </div>

      {/* Big image grid */}
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid md:grid-cols-2 gap-4 mb-16">
        {VERTICALS.map((v, i) => (
          <motion.a
            key={v.code}
            href="#"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: i * 0.08 }}
            className="group relative aspect-[16/10] overflow-hidden border border-white/10 block"
          >
            <img
              src={v.img}
              alt={v.label}
              className="absolute inset-0 w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-500 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-tr from-black/85 via-black/20 to-transparent" />
            <div className="absolute top-4 left-4 flex items-center gap-2">
              <span className="bg-[#D90429] text-white font-mono text-[10px] uppercase tracking-[0.25em] font-bold px-2 py-1">{v.code}</span>
              <span className="font-mono text-[10px] uppercase tracking-widest text-white/60">CATEGORY</span>
            </div>
            <div className="absolute bottom-0 left-0 right-0 p-6 flex items-end justify-between">
              <h3 className="font-display text-2xl md:text-4xl font-black tracking-tight uppercase leading-none">{v.label}</h3>
              <ArrowUpRight size={28} weight="bold" className="text-white group-hover:text-[#D90429] group-hover:rotate-12 transition-all" />
            </div>
          </motion.a>
        ))}
      </div>

      {/* Product marquee sticker row */}
      <div className="relative border-y border-white/10 py-6 bg-[#0d0d0f]">
        <Marquee gradient={false} speed={30}>
          {[
            { Icon: Heartbeat, l: "PATIENT MONITORS" }, { Icon: Cube, l: "ICU BEDS" }, { Icon: Lifebuoy, l: "OXYGEN CONCENTRATORS" },
            { Icon: Stethoscope, l: "DIAGNOSTICS" }, { Icon: Syringe, l: "SURGICAL DISPOSABLES" }, { Icon: FirstAid, l: "CONSUMABLES" },
            { Icon: Brain, l: "NEURO DEVICES" }, { Icon: Pulse, l: "VENTILATORS" }, { Icon: Waveform, l: "DEFIBRILLATORS" },
          ].map((p, i) => (
            <span key={i} className="mx-8 inline-flex items-center gap-3 font-display text-xl md:text-2xl font-bold tracking-tight uppercase text-white/90">
              <p.Icon size={22} weight="duotone" className="text-[#D90429]" />
              {p.l}
              <span className="text-white/25 mx-2">/</span>
            </span>
          ))}
        </Marquee>
      </div>
    </section>
  );
}

/* Hospital partners — grayscale marquee */
function Hospitals() {
  const hospitals = [
    "AIIMS · DELHI", "APOLLO · NOIDA", "FORTIS · MOHALI", "MAX · SAKET", "MEDANTA · GURGAON",
    "PGIMER · CHANDIGARH", "MANIPAL · JAIPUR", "NARAYANA · BANGALORE", "TATA MEMORIAL · MUMBAI",
    "SIR GANGA RAM · DELHI", "BLK-MAX · DELHI", "KOKILABEN · MUMBAI",
  ];
  return (
    <section className="relative bg-black text-white py-16 border-y border-white/10">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 mb-8">
        <div className="flex items-center gap-3">
          <LiveDot label="TRUSTED BY" />
          <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-white/50">74 institutions · India + SAARC</span>
        </div>
      </div>
      <Marquee gradient={false} speed={40} pauseOnHover>
        {hospitals.map((h) => (
          <span key={h} className="mx-10 font-display text-2xl md:text-3xl font-bold tracking-tight uppercase text-white/50 hover:text-white transition-colors cursor-default">
            {h}
          </span>
        ))}
      </Marquee>
      <div className="mt-4">
        <Marquee gradient={false} speed={30} direction="right">
          {hospitals.slice().reverse().map((h) => (
            <span key={`r-${h}`} className="mx-10 font-display text-xl md:text-2xl font-bold tracking-tight uppercase text-white/30 hover:text-[#D90429] transition-colors cursor-default">
              {h}
            </span>
          ))}
        </Marquee>
      </div>
    </section>
  );
}

/* Founder signature block */
function Founder({ onLogin }) {
  return (
    <section id="founder" className="relative bg-black text-white py-24 md:py-32 overflow-hidden">
      <div className="absolute -bottom-20 -left-10 h-[400px] w-[400px] rounded-full opacity-20 blur-[120px]" style={{ background: "#D90429" }} />

      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="grid md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-4 relative">
            <div className="aspect-[4/5] border-2 border-white/10 overflow-hidden relative">
              <img src={IMG_DOCTOR} alt="" className="w-full h-full object-cover grayscale" />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent" />
              <div className="absolute -bottom-2 -right-2">
                <Sticker rotate={4} color="#D90429">FOUNDER · 2019</Sticker>
              </div>
            </div>
          </div>

          <div className="md:col-span-8">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D90429] mb-4">/ A NOTE FROM THE FOUNDER</div>
            <blockquote className="font-display text-3xl md:text-5xl font-black tracking-tight leading-[1.05] mb-8">
              "I built Reyansh because I got tired of switching between eight tabs to answer <span className="text-[#D90429]">one</span> question.
              Now the team runs on one login. And I sleep better."
            </blockquote>
            <div className="flex items-center gap-4 mb-10">
              <div>
                <div className="font-display text-xl font-bold">Mukesh Panchal</div>
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/50">Founder · Shelves Tech Pvt Ltd</div>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-4">
              <button
                onClick={onLogin}
                data-testid="footer-login-button"
                className="group relative bg-[#D90429] text-white px-8 py-5 font-mono text-xs uppercase tracking-[0.2em] font-bold border-2 border-[#D90429] hover:bg-black hover:text-[#D90429] transition-colors inline-flex items-center gap-3"
                style={{ boxShadow: "6px 6px 0 #fff" }}
              >
                Sign in to Reyansh
                <ArrowUpRight size={18} weight="bold" className="group-hover:rotate-45 transition-transform" />
              </button>
              <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40">Google Workspace only · internal team</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* Massive footer wordmark */
function Footer() {
  return (
    <footer className="relative bg-black text-white pt-16 pb-8 border-t border-white/10 overflow-hidden">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="flex flex-wrap items-center justify-between gap-6 mb-12">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50 mb-2">DELIVERING BETTER HEALTHCARE · SINCE 2019</div>
            <div className="font-mono text-xs uppercase tracking-[0.2em] text-white/60">Shelves Tech Private Limited · Delhi · GSTIN 07AATCS6306H1Z0</div>
          </div>
          <div className="flex items-center gap-6 font-mono text-[10px] uppercase tracking-[0.25em] text-white/50">
            <a href="mailto:sales@shelvestech.com" className="hover:text-white transition">sales@shelvestech.com</a>
            <span>·</span>
            <a href="tel:+911234567890" className="hover:text-white transition">+91 · SHELVES</a>
          </div>
        </div>

        {/* Massive wordmark */}
        <div className="relative overflow-hidden -mx-6 md:-mx-10 border-t border-white/10 pt-8">
          <div className="font-display text-[22vw] font-black tracking-[-0.05em] leading-[0.85] text-white whitespace-nowrap select-none">
            REYANSH<span className="text-[#D90429]">.</span>
          </div>
        </div>

        <div className="mt-6 flex flex-wrap items-center justify-between gap-3 font-mono text-[10px] uppercase tracking-[0.25em] text-white/40">
          <span>© {new Date().getFullYear()} Shelves Tech Pvt Ltd · All rights reserved</span>
          <span>V1.6 · Built for internal use</span>
        </div>
      </div>
    </footer>
  );
}

/* ============ Root ============ */
export default function Landing() {
  const { user, loading } = useAuth();
  if (!loading && user) return <Navigate to="/dashboard" replace />;

  const handleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="relative bg-black text-white min-h-screen font-body" data-testid="landing-page-v2">
      <CursorDot />
      <Grain />
      <TopBar />
      <Nav onLogin={handleLogin} />
      <Hero onLogin={handleLogin} />
      <ModulesGrid />
      <StatsBand />
      <Verticals />
      <Hospitals />
      <Founder onLogin={handleLogin} />
      <Footer />
    </div>
  );
}
